import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button, Field, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import {
  useSettlement,
  useUpdateSettlement,
  useFinalizeSettlement,
  useReopenSettlement,
  useSettlementPosPull,
} from "../../app/hooks/useMoney.js";
import { pushToast } from "../../app/toasts.js";
import styles from "./dayOfShow.module.scss";

type SettlementDto = components["schemas"]["SettlementDto"];
type SettlementRead = components["schemas"]["SettlementRead"];
type UpdateSettlementRequest = components["schemas"]["UpdateSettlementRequest"];

// ---- money helpers (mirror SettlementPanel; the mobile card never re-derives a money authority) ----------

function money(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}
function intToStr(n: number | null | undefined): string { return n == null ? "" : String(n); }
function strToInt(s: string): number | undefined { const t = s.trim(); return t === "" ? undefined : Math.round(Number(t)); }

const SAVE_DEBOUNCE_MS = 600;

/**
 * The Day-of-Show settlement card — the phone money surface (ART-174). It is a SEPARATE, tighter card from the
 * desktop {@link SettlementPanel}: the handoff's `.dos-settle`, not the full booking worksheet. What the phone
 * shows is the night's numbers a person touches AT the door — Amount owed, the door counts, comps and the
 * merch cut — and nothing else. Splits and expenses are DESKTOP-ONLY entry (handoff), so this card never draws
 * them, and it carries their stored values forward untouched on every save.
 *
 * MONEY GATE — three layers:
 *   money:show_settlement → the settlement card
 *   money:per_person      → the "Your pay" chip only
 *   neither               → nothing (absent by construction; the endpoint is never called)
 *
 * `money:show_settlement` is the ART-174 narrowing (CapabilityEngine): a strict subset of money:full/per_show
 * minted ONLY for the two band roles the handoff names — band_member_av (artist owner + full member) and
 * tour_manager. Booking and management hold money:full but reach a band via the OrgLens, where their
 * EffectiveRole is the ORG role, so the token is withheld and "no dollar figure on DOS outside {artist, AV,
 * TM}" is structural. `canFull` (still money:full) is kept only to gate the audited reopen, which every
 * band-side settlement seat holds.
 */
export function DosSettlementCard({ showId, isFest, isGuestShow = false }: {
  showId: string; isFest: boolean; isGuestShow?: boolean;
}) {
  // Rules of Hooks: all capability reads are unconditional; the branch below is a pure boolean.
  const canSettle = useCapability("money:show_settlement");
  const canFull = useCapability("money:full");

  if (canSettle) return <DosSettlement showId={showId} isFest={isFest} canFull={canFull} isGuestShow={isGuestShow} />;
  return null;   // (the money:per_person "Your pay" chip was removed 2026-09-28 with the payouts it read)
}

// ---- money:full | money:per_show — the settlement card ---------------------------------------------------

/** The two door counts the phone lets you edit. Merch sales auto-populate from POS (read-only, Ryan ART-174);
 *  everything else on the DTO is round-tripped. */
type Edits = { gaSold: string; walkups: string };

function editsFromDto(d: SettlementDto): Edits {
  return {
    gaSold: intToStr(d.presaleSold),
    walkups: intToStr(d.walkupSold),
  };
}

/** Map a POS-pull outcome to the toast the card shows. */
function posPullMessage(state: string | null | undefined, gross: number | null | undefined): string {
  switch (state) {
    case "attached": return `Pulled ${money(gross)} in POS merch sales.`;
    case "already-attached": return "POS sales already synced for this show.";
    case "not-connected": return "No POS connected — enter sales manually.";
    case "window-open": return "POS sales are still open — pull after the show wraps.";
    case "no-timezone": return "Add the show’s timezone to pull POS sales.";
    default: return "Couldn’t reach your POS — try again."; // degraded / anything unexpected
  }
}

function DosSettlement({ showId, isFest, canFull, isGuestShow }: {
  showId: string; isFest: boolean; canFull: boolean; isGuestShow: boolean;
}) {
  const query = useSettlement(showId, true);
  const update = useUpdateSettlement();
  const finalize = useFinalizeSettlement();
  const posPull = useSettlementPosPull();

  const [edits, setEdits] = useState<Edits | null>(null);
  // The latest server read, held in a ref so the debounced save always fires with the CURRENT version — a
  // 409 otherwise lands on every second keystroke after a refetch bumps it.
  const readRef = useRef<SettlementRead | undefined>(undefined);
  readRef.current = query.data;

  // Auto-save on a ~600ms debounce — no save button (handoff). A full-replace PUT (ApplyScalars overwrites
  // every scalar), so the body carries FORWARD every desktop-entered field — prices, VIP, deposit, the merch
  // rates and the expense rows — or a door-count edit on the phone would wipe the desktop worksheet. Payouts
  // are append-only, so `addPayouts` is simply omitted. A failed write toasts (via useUpdateSettlement) and
  // stays retryable — the next keystroke re-fires it. Declared before any early return (Rules of Hooks).
  useEffect(() => {
    if (edits === null) return;   // nothing has been touched yet
    const t = setTimeout(() => {
      const cur = readRef.current;
      const dto = cur?.data;
      if (!dto) return;
      const body: UpdateSettlementRequest = {
        presaleSold: strToInt(edits.gaSold) ?? null,
        presalePriceCents: dto.presalePriceCents,
        walkupSold: strToInt(edits.walkups) ?? null,
        walkupPriceCents: dto.walkupPriceCents,
        vipCount: dto.vipCount,
        vipPriceCents: dto.vipPriceCents,
        // Merch sales are POS-attached server-side and read-only on the phone (Ryan, ART-174) — carry the
        // stored gross forward untouched so a door-count edit never wipes what the POS pull landed.
        merchGrossCents: dto.merchGrossCents,
        merchSoftGoodsPct: dto.merchSoftGoodsPct,
        merchRecordedPct: dto.merchRecordedPct,
        depositCents: dto.depositCents,
        depositReceivedOn: dto.depositReceivedOn,
      };
      update.mutate({ showId, version: cur?.version ?? 0, body });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // Re-run whenever the edited values change; `update`/`showId` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, showId]);

  const read = query.data;
  const d = read?.data;

  if (query.isLoading) {
    return (
      <section className={styles.settleCard} data-testid="dos-settlement">
        <Spinner size={18} />
      </section>
    );
  }
  if (query.isError || !d) {
    return (
      <section className={styles.settleCard} data-testid="dos-settlement">
        <div className={styles.settleHead}><span className={styles.sectionEyebrow}>Settlement</span></div>
        <p className={styles.empty}>Couldn’t load settlement.</p>
        <Button variant="ghost" size="sm" onClick={() => void query.refetch()}>Retry</Button>
      </section>
    );
  }

  const settled = d.status === "settled";
  const e = edits ?? editsFromDto(d);

  // The night's figures, from the SERVER — never recomputed here (the SettlementPanel rule). "Amount owed" is
  // what the PROMOTER still owes, so it is deal-based: the DEAL TAKE (deal − merch cut) less what has ARRIVED,
  // NOT the post-expense net to artist (ART-247/248 made `netToArtistCents` post-expense; the band's own §09
  // costs are not the promoter's debt). `dealTake = headlineTake − merchCut` from the DTO's line items, and the
  // only thing that has arrived is the DEPOSIT once its date is stamped — the server's `receivedCents` counts
  // exactly that (never the payouts ledger; see the DTO). It is therefore the SAME money as `depositIn`, so it
  // must be subtracted ONCE: subtracting both showed $0 owed on a $500 deposit against a $1,000 deal (Ryan,
  // ART-241). Floored at zero — an overpayment is a credit, not a negative owed. Merch SALES never enter it
  // (ART-290 counts them in the band's net to artist below, but the band sold them — the promoter does not owe
  // them); only the venue's cut, which comes off what the promoter hands over, does.
  const dealTake = (d.headlineTakeCents ?? 0) - (d.merchCutOwedCents ?? 0);
  const depositIn = d.depositReceivedOn ? (d.depositCents ?? 0) : 0;
  const owed = Math.max(0, dealTake - depositIn);

  const guarantee = d.guaranteeCents ?? 0;
  const via = doorShareCents(d) > guarantee ? "door split" : "guarantee";

  // Total revenue = (presale + walk-ups) × their stored prices — "tix × price". Tracks the counts as they are
  // typed; the prices are entered on desktop and only round-tripped here.
  const totalRev =
    (strToInt(e.gaSold) ?? 0) * (d.presalePriceCents ?? 0)
    + (strToInt(e.walkups) ?? 0) * (d.walkupPriceCents ?? 0);

  const merchCutOn = d.merchSoftGoodsPct != null || d.merchRecordedPct != null;
  // One headline merch % for the "Merch cut" box — soft goods is the common venue cut; 0% when no deal.
  const merchPct = d.merchSoftGoodsPct ?? d.merchRecordedPct ?? 0;

  function commit(next: Edits) {
    setEdits(next);
  }

  if (settled) {
    return <DosSettled d={d} owed={owed} via={via} canFull={canFull} showId={showId} />;
  }

  return (
    <section className={styles.settleCard} data-testid="dos-settlement">
      {/* No status badge on a pending card — it says nothing the section heading doesn't (Ryan, ART-174). */}
      <div className={styles.settleHead}>
        <span className={styles.sectionEyebrow}>Settlement</span>
      </div>

      {/* Amount owed — the card's headline, with the deal total and the deposit under it (Ryan, ART-174). */}
      <div className={styles.settleOwedBlock}>
        <span className={styles.settleOwedLabel}>Amount owed</span>
        <span className={styles.settleOwed} data-testid="amount-owed">{money(owed)}</span>
        <span className={styles.settleOwedSub} data-testid="settlement-sub">
          {money(dealTake)} Total · {money(depositIn)} Deposit
        </span>
      </div>

      {/* A festival is a flat guarantee — no door to count. */}
      {isFest ? (
        <div className={styles.settleReadRow}>
          <ReadCell label="Guarantee">{money(guarantee)}</ReadCell>
          <ReadCell label="Comps" testId="comps">{d.compsCount ?? 0}</ReadCell>
        </div>
      ) : (
        // A 3-across grid (Ryan, ART-174): presale · walk-ups · comps, then merch sales · cut · owed, then a
        // full-width total. Only the two door counts are Fields; Comps derives live from the guest list, and
        // Merch sales / Merch cut / Cut owed / Total revenue are all server-computed read cells.
        <div className={styles.settleGrid}>
          <Field
            label="Presale" density="compact" type="number" inputMode="numeric" min="0" step="1"
            placeholder="0" value={e.gaSold} onChange={(ev) => commit({ ...e, gaSold: ev.target.value })}
          />
          <Field
            label="Walk-ups" density="compact" type="number" inputMode="numeric" min="0" step="1"
            placeholder="0" value={e.walkups} onChange={(ev) => commit({ ...e, walkups: ev.target.value })}
          />
          <ReadCell label="Comps" testId="comps">{d.compsCount ?? 0}</ReadCell>
          {/* Merch sales auto-populate from POS and are read-only here (Ryan, ART-174) — the "Pull POS sales"
              action below refreshes them; the desktop worksheet is where a manual figure is entered. */}
          <ReadCell label="Merch sales" testId="merch-sales">{money(d.merchGrossCents)}</ReadCell>
          {/* 0% when no deal is on file, so the spot always reads (Ryan, ART-174). */}
          <ReadCell label="Merch cut" testId="merch-cut-pct">{merchPct}%</ReadCell>
          <ReadCell label="Cut owed" testId="merch-cut-owed">{money(d.merchCutOwedCents)}</ReadCell>
          {/* Total revenue is COMPUTED (tix × price) — full-width, dashed to mark it as not an input. */}
          <ReadCell label="Total revenue" dashed wide testId="total-rev">{money(totalRev)}</ReadCell>
        </div>
      )}

      {/* Pull tonight's merch gross from POS (Shopify/Square) when a merch deal is on file — fill-only, so a
          hand-typed figure is never clobbered. Not on a GUEST show (ART-249): the pull keys its sales window off
          the host's show timezone, which a guest band cannot read, so it could only ever answer "no timezone". */}
      {!isFest && !isGuestShow && merchCutOn && (
        <Button
          variant="ghost" size="sm" className={styles.settleMerchPull} loading={posPull.isPending}
          data-testid="pull-pos"
          onClick={() => posPull.mutate(showId, {
            onSuccess: (r) => pushToast(posPullMessage(r.state, r.merchGrossCents)),
            onError: () => pushToast("Couldn’t reach your POS — try again."),
          })}
        >
          Pull POS sales
        </Button>
      )}

      {/* Crimson on the DOS surface is the fixed `--dos-accent-hi`, NOT the theme's brightened dark-mode
          accent (`variant="accent"` = washed rose) — the settle CTA must match the LIVE pill + chips it sits
          with (Ryan, ART-174). `.settleGo` re-paints the accent button in the DOS crimson. */}
      <Button
        variant="accent" size="md" className={clsx(styles.settleAction, styles.settleGo)}
        onClick={() => finalize.mutate(showId)} loading={finalize.isPending}
      >
        Mark settled
      </Button>
    </section>
  );
}

// ---- settled — locked, with an audited reopen (money:full) -----------------------------------------------

function DosSettled({
  d, owed, via, canFull, showId,
}: { d: SettlementDto; owed: number; via: string; canFull: boolean; showId: string }) {
  const reopen = useReopenSettlement();
  const [reason, setReason] = useState<string | null>(null);
  const settledOn = d.settledOn
    ? new Date(d.settledOn).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  return (
    <section className={styles.settleCard} data-testid="dos-settlement">
      <div className={styles.settleHead}>
        <span className={styles.sectionEyebrow}>Settlement</span>
        <span className={styles.settleStatus} data-status="settled">
          {settledOn ? `Settled ${settledOn}` : "Settled"}
        </span>
      </div>

      <div className={styles.settleOwedBlock}>
        <span className={styles.settleOwedLabel}>Amount owed</span>
        <span className={styles.settleOwed} data-testid="amount-owed">{money(owed)}</span>
        <span className={styles.settleOwedSub}>
          {money(d.netToArtistCents)} net · via <b>{via}</b>
        </span>
      </div>

      <div className={styles.settleReadRow}>
        <ReadCell label="Comps" testId="comps">{d.compsCount ?? 0}</ReadCell>
        <ReadCell label="Net to artist" testId="net-to-artist">{money(d.netToArtistCents)}</ReadCell>
      </div>

      {/* Reopen is money:full only and audited — it collects a reason, then re-opens (settled → in_progress).
          The button is the handoff's "Settled ✓, tap to reopen". */}
      {canFull && (
        reason === null ? (
          <Button variant="ghost" size="md" className={styles.settleAction} onClick={() => setReason("")}>
            Settled ✓, tap to reopen
          </Button>
        ) : (
          <div className={styles.settleReopen}>
            <Field
              label="Reason for reopening" density="compact" placeholder="Why is this being reopened?"
              value={reason} onChange={(ev) => setReason(ev.target.value)}
            />
            <div className={styles.settleReopenActions}>
              <Button variant="ghost" size="sm" onClick={() => setReason(null)}>Cancel</Button>
              <Button
                variant="accent" size="sm" className={styles.settleGo} loading={reopen.isPending}
                disabled={reason.trim() === ""}
                onClick={() => reopen.mutate({ showId, body: { reason: reason.trim() } })}
              >
                Reopen
              </Button>
            </div>
          </div>
        )
      )}
    </section>
  );
}

/**
 * The door share as stored — `pct% of max(0, grossDoor − threshold)`, off the DTO's own figures. A display
 * echo of `SettlementCalculator`, used ONLY to name which side of the "vs guarantee" the net came from; the
 * net itself is the server's. (Settlement expenses were removed 2026-09-28, so the split is on the full door.)
 */
function doorShareCents(d: SettlementDto): number {
  const pct = d.doorSplitPct ?? 0;
  if (pct <= 0) return 0;
  const grossDoor =
    (d.presaleSold ?? 0) * (d.presalePriceCents ?? 0)
    + (d.walkupSold ?? 0) * (d.walkupPriceCents ?? 0)
    + (d.vipCount ?? 0) * (d.vipPriceCents ?? 0);
  const splittable = Math.max(0, grossDoor - (d.doorSplitAfterCents ?? 0));
  return Math.round((splittable * pct) / 100);
}

/**
 * A read-only labelled value shaped to MATCH a compact {@link Field}: the label sits above a box of the shared
 * `--ac-control-h` height, so a read cell and an input cell in the same grid row line up exactly (Ryan,
 * ART-174 — the boxes had drifted to different heights). The box is filled (vs the input's outline) so it
 * still reads as not-an-input; `dashed` marks the computed total, `wide` spans the full grid row.
 */
function ReadCell({
  label, dashed, wide, testId, children,
}: { label: string; dashed?: boolean; wide?: boolean; testId?: string; children: React.ReactNode }) {
  return (
    <div className={styles.settleRead} data-wide={wide || undefined}>
      <span className={styles.settleReadLabel}>{label}</span>
      <div className={styles.settleReadBox} data-dashed={dashed || undefined}>
        <span className={styles.settleReadValue} data-testid={testId}>{children}</span>
      </div>
    </div>
  );
}
